/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useState, useContext } from "react";
import styled from "styled-components";
import Text from "./Text";
import { useRovingTabIndex } from "../../hooks/a11y/useRovingTabIndex";
import { isActivationKey } from "../../hooks/a11y/activationKeys";
import { useTranslate } from "../../hooks/useTranslate";

const StyledRadioCardsGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const RadioCardsWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StyledRadioCardItem = styled.li`
  padding: 7px 16px;
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
  line-height: 1.25;
  font-weight: 600;
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus,
  &:focus-visible {
    border-color: rgba(${({ theme }) => theme.colors.accent.rgba});
  }

  @media (hover: hover) {
    &:hover {
      border-color: rgba(${({ theme }) => theme.colors.accent.rgba});
    }
  }

  ${(props) => props.$isSelected && `border-color: rgba(${props.theme.colors.accent.rgba});`}
`;

const RadioCardContext = createContext();

const RadioCardProvider = ({ children }) => {
  const [selectedValue, setSelectedValue] = useState(null);

  const handleValueChange = (value) => {
    setSelectedValue(value);
  };

  return <RadioCardContext.Provider value={{ selectedValue, handleValueChange }}>{children}</RadioCardContext.Provider>;
};

const RadioCardsGroup = ({ label, children }) => {
  return (
    <RadioCardProvider>
      <StyledRadioCardsGroup>
        <Text color="dark">{label}</Text>
        <RadioCardsList label={label}>{children}</RadioCardsList>
      </StyledRadioCardsGroup>
    </RadioCardProvider>
  );
};

const RadioCardsList = ({ label, children }) => {
  const { _t } = useTranslate();
  const { selectedValue } = useContext(RadioCardContext);
  const childArray = React.Children.toArray(children);
  const selectedIndex = Math.max(0, childArray.findIndex((child) => child.props.value === selectedValue));
  const { getItemProps } = useRovingTabIndex({
    count: childArray.length,
    orientation: "horizontal",
    defaultIndex: selectedIndex,
  });

  return (
    <RadioCardsWrapper role="radiogroup" aria-label={_t(label)}>
      {childArray.map((child, index) => React.cloneElement(child, { rovingProps: getItemProps(index) }))}
    </RadioCardsWrapper>
  );
};

const RadioCardItem = ({ value, children, onChange, onClick, rovingProps = {} }) => {
  const { selectedValue, handleValueChange } = useContext(RadioCardContext);
  const isSelected = selectedValue === value;

  const handleClick = () => {
    handleValueChange(value);
    onChange?.(value);
    onClick?.(value);
  };

  const handleKeyDown = (e) => {
    rovingProps.onKeyDown?.(e);
    if (isActivationKey(e)) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <StyledRadioCardItem
      ref={rovingProps.ref}
      role="radio"
      aria-checked={isSelected}
      tabIndex={rovingProps.tabIndex ?? 0}
      $isSelected={isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </StyledRadioCardItem>
  );
};

export { RadioCardsGroup, RadioCardItem };
